import type { Property, Entity, AsyncValueResolver, Type, EntityOfType } from '@cognitoforms/model.js';
import { Property$pendingInit, isEntityType } from '@cognitoforms/model.js';
import type { FormsModel, FormsModelOptions } from 'src/framework/forms-model';
import type { ObjectLookup } from 'src/util';
import type EntryViewService from 'src/web-api/entry-views/entry-view-service';
import type { CascadeFilterInfo, CascadeFilterManagerFactory } from './cascade-filter-manager';
import CascadeFilterManager from './cascade-filter-manager';
import type { LookupIndexInfo, LookupManagerFactory } from './lookup-manager';
import LookupManager from './lookup-manager';
import { PersonLabelFormat } from './person-label-format';
import { getFormEntry } from '../../core';
import type { FormEntryIndex } from '@cognitoforms/types/server-types/forms/model/form-entry-index';
import { getHttpError } from 'src/web-api/base-service';
import type Log from 'src/framework/logging/log';
import { ensureError } from 'src/framework/logging/error';
import type { EntryData } from 'src/web-api/entry-views/entry-view-types';
import { isLookupDynamicEntity } from 'src/util/model';
import type { LookupData } from 'src/web-api/entry-views/entry-view-service';

const lookupFieldInfoKey = '$lookupFieldInfo';
const cascadeFilterInfoKey = '$cascadeFilterInfo';

function isRootLookupProperty(propDef: any) {
	return typeof propDef === 'object' && propDef[lookupFieldInfoKey]?.indexTypeName;
}

function isNestedLookupProperty(propDef: any) {
	return typeof propDef === 'object' && propDef[lookupFieldInfoKey] && !propDef[lookupFieldInfoKey]?.indexTypeName;
}

function isCascadeFilterProperty(propDef: any) {
	return typeof propDef === 'object' && propDef[cascadeFilterInfoKey];
}

function getManagerPropName(lookupProperty: Property) {
	return `${lookupProperty.name}_LookupManager`;
}

function getIsIncludedMethodName(lookupProperty: Property) {
	return `${lookupProperty.name}_IsIncluded`;
}

/**
 * Determines whether a lookup index should be included - either there is no filter,
 * or the index passes the filter.
 * @param container The object that contains the lookup field
 * @param lookupProperty The property for the lookup field
 * @param index The index option to test
 * @returns True, if the index should be an included option for the lookup
 */
export function isIncluded(container: Entity, lookupProperty: Property, index: EntityOfType<FormEntryIndex>) {
	try {
		const isIncludedMethod = getIsIncludedMethodName(lookupProperty);

		// Include the index if there is no filter
		if (!container[isIncludedMethod])
			return true;

		// Invoke the "InIncluded" expression
		return container[isIncludedMethod](index);
	}
	catch (e) {
		const entityType = container?.meta?.type?.fullName ?? '[UnknownType]';
		const property = lookupProperty?.name ?? '[UnknownProperty]';
		const indexId = index?.Id ?? '[UnknownId]';
		console.warn(`Error in isIncluded: property '${property}' (type '${entityType}') for index '${indexId}':`, e);
		return false;
	}
}

/**
 * Adds a rule to the specified type which ensures a lookup manager's filtered indexes are
 * kept up to date as dependent model properties change.
 */
function addLookupFilterRules(type: Type, lookupProperty: Property, lookupInfo: LookupIndexInfo): void {
	// Don't allow a lookup to be prefilled with invalid values
	lookupProperty.changed.subscribe(function () {
		const entry = getFormEntry(this);
		if (entry.meta.isNew) {
			const manager = this[getManagerPropName(lookupProperty)] as LookupManager;
			if (manager)
				manager.whenReady(() => manager.clearInvalidValues());
		}
	});

	// Ensure that lookup value(s) is/are valid when a filter predicate changes
	type.addRule({
		name: `${lookupProperty.containingType.fullName}.${lookupProperty.name}.UpdateFilteredIndexes`,
		execute() {
			const manager = this[getManagerPropName(lookupProperty)] as LookupManager;
			if (manager)
				manager.whenReady(() => manager.updateFilteredIndexes(true));
		},
		onChangeOf: type.getPaths(`{${lookupInfo.filterPaths.join(',')}}`)
	}).register();
}

function getIsDefaultedMethodName(lookupProperty: Property) {
	return `${lookupProperty.name}_IsDefaulted`;
}

/**
 * Determines if the index should be a default option based solely on static data on the index itself
 */
function isStaticallyDefaulted(option: EntityOfType<FormEntryIndex>) {
	return option.AdditionalValues.some(v => v.FieldId === 'Default' && v.Value === 'True');
}

/**
 * Determines whether a lookup index should be a default, based on either static
 * default criteria on the index, or the dynamic IsDefaulted expression.
 * @param container The object that contains the lookup field
 * @param lookupProperty The property for the lookup field
 * @param index The index option to test
 * @returns True, if the index should be a default value for the lookup
 */
export function isDefaulted(container: Entity, lookupProperty: Property, index: EntityOfType<FormEntryIndex>) {
	if (isStaticallyDefaulted(index))
		return true;

	try {
		const isDefaultedMethod = getIsDefaultedMethodName(lookupProperty);

		if (!container[isDefaultedMethod])
			return false;

		return container[isDefaultedMethod](index);
	}
	catch (e) {
		const entityType = container?.meta?.type?.fullName ?? '[UnknownType]';
		const property = lookupProperty?.name ?? '[UnknownProperty]';
		const indexId = index?.Id ?? '[UnknownId]';
		console.warn(`Error in isDefaulted: property '${property}' (type '${entityType}') for index '${indexId}':`, e);
		return false;
	}
}

/**
 * Adds rules to the specified type which ensures a lookup manager's default indexes are
 * established and default filter is applied initially, and as as dependent model properties change.
 */
function addLookupDefaultRules(type: Type, lookupProperty: Property, lookupInfo: LookupIndexInfo): void {
	// Apply the default filter when the entry is initialized
	// - for new entries if the value has not been prefilled
	// - for existing entries when there is no persisted value
	type.addRule({
		name: `${lookupProperty.containingType.fullName}.${lookupProperty.name}.SetInitialDefaultValue`,
		execute() {
			const manager = this[getManagerPropName(lookupProperty)] as LookupManager;
			if (manager) {
				manager.whenReady(() => {
					// Wait for the entry to initialize to ensure that prefilled or persisted lookup values have been resolved
					getFormEntry(this).initialized.then(() => {
						const isInited = lookupProperty.isInited(this) && !Property$pendingInit(this, lookupProperty);
						if (!isInited)
							manager.updateDefaultIndexes(true);
					});
				});
			}
		}
	}).onInit().register();

	// Apply the default filter whenever one of the default filter predicates changes
	type.addRule({
		name: `${lookupProperty.containingType.fullName}.${lookupProperty.name}.UpdateDefaultIndexesAndSetDefaultValue`,
		execute() {
			const manager = this[getManagerPropName(lookupProperty)] as LookupManager;
			if (manager)
				manager.whenReady(() => manager.updateDefaultIndexes(true));
		},
		onChangeOf: type.getPaths(`{${lookupInfo.defaultPaths.join(',')}}`)
	}).register();

	// Ensure that a default filter rule will run if a calculation that it depends on is changed.
	// A property with a default value rule may have a persisted value, in which case it will not run unless one of its predicates
	// fires a change event. A calculation will not fire a change event the first time it runs if it didn't previously have a value,
	// which may be the case for existing instances if the calculation is never accessed (ex: a hidden field), or is not accessed
	// prior to the event that would trigger it to change (i.e. setting the default action). So, in order to ensure that the default
	// filter's calculated predicates fire a change event, we must ensure that the calculation is accessed when the object is initialized.
	type.addRule({
		name: `${lookupProperty.containingType.fullName}.${lookupProperty.name}.AccessDefaultValuePredicates`,
		execute() {
			type.getPaths(`{${lookupInfo.defaultPaths.join(',')}}`).forEach(p => p.value(this));
		}
	}).onInitExisting().register();
}

export function extendModelWithLookups(
	model: FormsModel,
	options: FormsModelOptions,
	entryViewService: EntryViewService,
	getLookupManager: LookupManagerFactory,
	getCascadeFilterManager: CascadeFilterManagerFactory,
	log: Log,
	pseudoLoadDeleted: boolean,
	suppressLookupLoadingErrors: boolean
): AsyncValueResolver {
	/**
	 * Adds the LookupManger property to the lookup manager's containingType.
	 */
	function addLookupManagerProperty(lookupProperty: Property, lookupInfo: LookupIndexInfo, entryViewService: EntryViewService, getLookupManager: LookupManagerFactory) {
		lookupProperty.containingType.extend({
			[getManagerPropName(lookupProperty)]: {
				get: function(this: Entity) {
					return getLookupManager({
						lookupInfo,
						indexType: model.resolveType(lookupInfo.indexTypeName),
						entryViewService,
						lookupProperty,
						container: this
					});
				},
				type: LookupManager as any
			}
		});

		lookupProperty.containingType.addRule(function autoCreateLookupManager(this: Entity) {
			// force the manager to be created by accessing the property
			this.get(getManagerPropName(lookupProperty));
		}).onInit().register();
	}

	/**
	 * Adds the CascadeFilterManager property to the cascade filter manager's containingType.
	 */
	function addCascadeFilterManagerProperty(cascadeProperty: Property, filterInfo: CascadeFilterInfo, getCascadeFilterManager: CascadeFilterManagerFactory) {
		const cascadeManagerProp = `${cascadeProperty.name}_CascadeFilterManager`;
		cascadeProperty.containingType.extend({
			[cascadeManagerProp]: {
				get: {
					function() {
						return getCascadeFilterManager({
							lookupManager: this[`${filterInfo.lookupPropertyName}_LookupManager`],
							cascadeProperty,
							indexProperty: model.resolveType(filterInfo.indexTypeName).getProperty(filterInfo.indexPropertyName),
							priorFilters: filterInfo.priorFilters
						});
					},
					dependsOn: `${filterInfo.lookupPropertyName}_LookupManager`
				},
				type: CascadeFilterManager as any
			}
		});

		cascadeProperty.containingType.addRule(function autoCreateCascadeManager() {
			// force the manager to be created by accessing the property
			this.get(cascadeManagerProp);
		}).onInit().register();
	}

	const lookupFieldInfo = new Map<Property, LookupIndexInfo>();

	/**
	 * Traverse the model definition and do the following:
	 * - harvest lookup view ids for all lookup fields
	 * - create rules to update filtered/default options for lookup fields
	 * - create rules to initialize lookup/cascade filter managers for any type containing a lookup field
	 */
	for (const [typeName, typeDef] of Object.entries(options)) {
		if (!typeName.startsWith('$')) {
			const currentType = model.resolveType(typeName);
			const cascadeManagersToInitialize = [];
			for (const [propName, propDef] of Object.entries(typeDef)) {
				const currentProperty = currentType.getProperty(propName);

				const lookupInfo = propDef[lookupFieldInfoKey] as LookupIndexInfo;
				if (lookupInfo?.emailFieldPath && currentProperty.format)
					currentProperty.format = new PersonLabelFormat(currentProperty.format, lookupInfo.emailFieldPath);

				// lookups on secondary forms (not the root) only need the viewId to load entries
				if (isNestedLookupProperty(propDef)) {
					lookupFieldInfo.set(currentProperty, lookupInfo);
				}
				else if (isCascadeFilterProperty(propDef)) {
					if (!options.$disableLookupFiltering)
						cascadeManagersToInitialize.push(() => addCascadeFilterManagerProperty(currentProperty, propDef[cascadeFilterInfoKey] as CascadeFilterInfo, getCascadeFilterManager));
				}
				// lookups on the root form need more metadata to describe the index type for displaying options
				else if (isRootLookupProperty(propDef)) {
					lookupFieldInfo.set(currentProperty, lookupInfo);

					if (!options.$disableLookupFiltering) {
						addLookupManagerProperty(currentProperty, lookupInfo, entryViewService, getLookupManager);

						if (lookupInfo.filterPaths)
							addLookupFilterRules(currentType, currentProperty, lookupInfo);

						if (lookupInfo.defaultPaths)
							addLookupDefaultRules(currentType, currentProperty, lookupInfo);
					}
				}
			}

			if (cascadeManagersToInitialize.length)
				cascadeManagersToInitialize.forEach(addCascadeFilterManager => addCascadeFilterManager());
		}
	}

	/**
	 * Attempts to load form entries for lookup properties with a corresponding lookup view using the configured EntryViewService.
	 */
	function lookupEntryResolver(instance: Entity, property: Property, value: any): Promise<Entity | EntryData | LookupData | (Entity | EntryData | LookupData)[]> | null {
		const lookupInfo = lookupFieldInfo.get(property);
		const viewId = lookupInfo?.viewId;
		const valueIsList = Array.isArray(value);
		const expectedIsList = property.isList;
		if (viewId && value && isEntityType(property.propertyType)) {
			// Multi→Single migration: only load the first value to avoid wasted lookups
			const ids = valueIsList && !expectedIsList
				? (value.length > 0 ? [value[0]] : [])
				: valueIsList ? value as any[] : [value];
			const loaded: ObjectLookup<Entity | EntryData> = {};
			const isV2Lookup = isLookupDynamicEntity(property);

			// determine which entries are already in memory
			for (const id of ids) {
				const entry = model.getEntityById(property.propertyType.meta, id);
				if (entry)
					loaded[id] = entry;
			}

			const unloadedIds = ids.filter(id => !loaded[id]);
			let dataLoaded: Promise<any> = Promise.resolve();

			// load remote entry data if necessary
			if (unloadedIds.length) {
				const rootEntry = getFormEntry(instance);
				const fieldPath = lookupInfo.fieldPath;
				dataLoaded = entryViewService.lookupEntries(viewId, unloadedIds, rootEntry, {
					isV2Lookup,
					fieldPath,
					containingFormId: lookupInfo.containingFormId,
					referencesHash: lookupInfo.referencesHash
				})
					.then(entries => {
						// Record data that was loaded via the /entry-lookup endpoint
						entries.forEach(data => (loaded[data.Id] = data));
					})
					.catch(reason => {
						// Capture the underlying HTTP error when lookup resolution fails on a request.
						const networkError = getHttpError(reason);

						if (log) {
							const additionalProperties: Record<string, unknown> = {};
							additionalProperties['Source'] = 'lookupEntryResolver';
							additionalProperties['Value'] = value;
							if (networkError) {
								additionalProperties['Url'] = networkError.config?.url;
								additionalProperties['Method'] = networkError.config?.method;
								additionalProperties['Timeout'] = networkError.config?.timeout;
								additionalProperties['Code'] = networkError.code;
							}
							log.error(ensureError(reason, 'An unknown error occurred resolving lookup values'), additionalProperties);
						}

						if (!suppressLookupLoadingErrors && networkError)
							throw reason;

						console.warn('Unable to resolve lookup value:', value, reason);
					});
			}

			// return all entries in order based on provided value
			return dataLoaded.then(() => {
				for (const unloaded of unloadedIds) {
					if (!loaded[unloaded]) {
						if (pseudoLoadDeleted)
							loaded[unloaded] = { Id: 'Deleted|' + unloaded } as any;
					}
				}

				return ids.map(id => loaded[id]);
			}).then(entries => expectedIsList ? entries.filter(entry => entry !== undefined) : (entries[0] !== undefined ? entries[0] : null));
		}

		// return null so the framework knows there is no async resolution for this value
		else
			return null;
	}

	return lookupEntryResolver;
}
